import { updateMetaFromContext } from './meta-registry.js';

/**
 * # Tab Route Mixin Class (For Views)
 * 
 * Use this mixin to add support for linking an `<sl-tab-group>` with sub-routes of a view.
 * To begin using this mixin, you should already have a view created (inheriting from ViewBase)
 * which has a `<sl-tab-group>` element in the template.
 * 
 * ## How to Use
 * 
 * ### Step 1 - Setup Routes
 * 
 * Make sure there are routes for all of your sub-tab views in `routes.js`. They should each set
 * an extra `tab` parameter in the route params. Put the root route last.
 * 
 * Example (BEFORE):
 * 
 * ```javascript
 * page('/projects/:key', (c) => routeTo('project-view', 'public', c));
 * ```
 * 
 * Example (AFTER):
 * 
 * ```javascript
 * page('/projects/:key/about', (c) => routeTo('project-view', 'public', c, { tab: 'about' }));
 * page('/projects/:key/members', (c) => routeTo('project-view', 'public', c, { tab: 'members' }));
 * page('/projects/:key', (c) => routeTo('project-view', 'public', c, { tab: 'home' }));
 * ```
 * 
 * ### Step 2 - Add the Mixin
 * 
 * Add the mixin to your view class:
 * 
 * ```javascript
 * import { TabRouteMixin } from '../../modules/tab-route-mixin.js';
 * 
 * // ...
 * 
 * export class ProjectView extends TabRouteMixin(BaseProjectViewMixin(ViewBase)) {
 * // ...
 * 
 * ```
 * 
 * ### Step 3 (Recommended) - Create a TAB_SETTINGS constant
 * 
 * It's helpful to create a constant to store the tab settings for your view. Here's an example.
 * 
 * ```javascript
 * const TAB_SETTINGS = (project) => ({
 *   home: {
 *     path: `/projects/${project.key}`,
 *     title: `${project.name} - q1 Network`,
 *   },
 *   about: {
 *     path: `/projects/${project.key}/about`,
 *     title: `About: ${project.name} - q1 Network`,
 *   },
 *   members: {
 *     path: `/projects/${project.key}/members`,
 *     title: `Members: ${project.name} - q1 Network`,
 *   },
 * });
 * ```
 * 
 * ### Step 4 - Add required methods to your view class
 * 
 * There are two required methods you need to overwrite.
 * 
 * ```javascript
 * _getTabPath(tabKey) {
 *   if (!this.project) return window.location.pathname;
 * 
 *   return TAB_SETTINGS(this.project)[tabKey]?.path || window.location.pathname;
 * }
 * 
 * _getTabTitle(tabKey) {
 *   if (!this.project) return 'Project View - q1 Network';
 * 
 *   return TAB_SETTINGS(this.project)[tabKey]?.title || 'Project View - q1 Network';
 * }
 * ```
 * 
 * You may also want to remove any other logic you have in the view already which sets the 
 * document title. It will be unnecessary once this mixin is in place.
 * 
 * ### Step 5 - Mark up your `<sl-tab-group>` element
 * 
 * Add the `data-tab-route="source"` attribute to your `<sl-tab-group>` element.
 * Also, add an event listener for the `sl-tab-show` event.
 * 
 * **Before:**
 * 
 * ```html
 * <sl-tab-group id="project-tabs">
 *   <sl-tab slot="nav" panel="home">Home</sl-tab>
 *   <sl-tab slot="nav" panel="about">About</sl-tab>
 *   <sl-tab slot="nav" panel="members">Members</sl-tab>
 *   <!-- ... -->
 * ```
 * 
 * **After:**
 * 
 * ```html
 * <sl-tab-group id="project-tabs" data-tab-route="source" @sl-tab-show="${this.handleTabShow}">
 *   <sl-tab slot="nav" panel="home">Home</sl-tab>
 *   <sl-tab slot="nav" panel="about">About</sl-tab>
 *   <sl-tab slot="nav" panel="members">Members</sl-tab>
 *   <!-- ... -->
 * ```
 * 
 * ### Step 6 - Implement `initComplete` if needed
 * 
 * This is almost certainly needed if you have things which need to load before the tab group is
 * rendered. For example, you might need to wait until the view mode resolves before calling
 * to show the initial tab.
 * 
 * The tab route mixin will check for `this.initComplete` on page load. If it exists, the tab 
 * route mixing assumes it's a promise and waits for it to resolve before setting the initial tab.
 * 
 * The easiest way to implement this is to use the built in methods in ViewBase:
 * 
 * **First, add `this.resetInitParams()` to the top of your constructor:**
 * 
 * ```javascript
 * constructor() {
 *   super();
 * 
 *   // Begin by creating a `this.initComplete` promise that resolves when the view is initialized.
 *   this.resetInitParams();
 * 
 *   // ...
 * }
 * ```
 * 
 * ** Then call `this._resolveInit()` when your view is fully loaded and the tab group is rendered
 * or is in the process of rendering:**
 * 
 * ```javascript
 * // ... inside your `_initView()` method or similar ...
 * this.viewMode = 'loaded';
 * this._resolveInit(); // resolves the `this.initComplete` promise
 * ```
 * 
 * ### That's it! 
 * 
 * Now you should be able to navigate to your view with the tab route in the URL and it should
 * automatically select the correct tab and update the document title.
 * 
 * And when you click through the tabs, the URL and document title should update accordingly.
 * 
 * And everything should play nicely with the browser history / back & forward buttons.
 * 
 */
export const TabRouteMixin = (SuperClass) => class extends SuperClass {

  /*=== OVERWRITEABLE METHODS ===*/

  /**
   * REQUIRED OVERWRITE. Should return the path for the given tab key.
   */
  _getTabPath(tabKey) {
    return window.location.pathname;
  }

  /**
   * REQUIRED OVERWRITE. Should return the doc title for the given tab key.
   */
  _getTabTitle(tabKey) {
    return 'Tab Route Mixin Not Configured';
  }

  /**
   * OPTIONAL OVERWRITE. Overwrite this if you'd like to use different logic.
   * THIS MUST BE A `<sl-tab-group>` ELEMENT.
   */
  get tabRouteSourceElement() {
    return this.renderRoot.querySelector('sl-tab-group[data-tab-route="source"]');
  }

  /*=== INITIALIZATION ===*/

  static properties = {
    ...super.properties,

    tab: { type: String },
  };

  constructor() {
    super();

    this.tab = null;
  }

  /*=== LIFECYCLE ===*/

  updated(changedProperties) {
    super.updated(changedProperties);

    if (changedProperties.has('routeParams')) {
      if (this.initComplete === undefined || this.initComplete === null) {
        this.handleTabRouteParamsUpdate();
      } else {
        this.initComplete.then(() => {
          this.handleTabRouteParamsUpdate();
        });
      }
    }
  }

  /*=== HANDLERS ===*/

  async handleTabRouteParamsUpdate() {
    const newTabKey = this.routeParams?.tab || 'home';

    if (newTabKey === this.tab) return;
    
    // Check that the tab is valid, if not redirect to the default tab
    const path = this._getTabPath(newTabKey);
    if (window.location.pathname !== path) {
      console.error(`Invalid tab key: ${newTabKey}`);
      this.navigateTo(path);
      return;
    }

    this.tab = newTabKey;
    
    const metaContext = this.metaContext;
    if (metaContext) {
      updateMetaFromContext(this.appElement?.currentRoute, metaContext);
      if (metaContext.docTitle) document.title = metaContext.docTitle;
    }

    if (!this.tabRouteSourceElement) await this.updateComplete;
    if (!this.tabRouteSourceElement) {
      console.error('Tab route source element not found.');
      return;
    }

    await this.tabRouteSourceElement.updateComplete;
    this.tabRouteSourceElement.show(this.tab);
  }

  handleTabShow(event) {
    const newTabKey = event.detail.name;

    this.tab = newTabKey;

    const path = this._getTabPath(this.tab);
    const metaContext = this.metaContext;

    if (metaContext) {
      updateMetaFromContext(this.appElement?.currentRoute, metaContext);
      if (metaContext.docTitle) document.title = metaContext.docTitle;
    }

    if (window.location.pathname !== path) this.navigateTo(path);
  }

}
